import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, Lightbulb, Maximize2, Minimize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DynamicsDiagnostics, QualityApi } from '../qualityApi';
import { ResolvedPeriod } from '../../PeriodPicker';
import { useDefaultBoardId } from '../../../../shared/hooks';
import { BucketDrilldownModal } from './BucketDrilldownModal';
import { useDynamicsBatchEntry } from './DynamicsBatchContext';
import { DynamicsChartLayoutProvider } from './DynamicsChartLayoutContext';
import { DynamicsChartZoomShell } from './DynamicsChartZoomShell';

type DynamicsMetric = Parameters<typeof QualityApi.getDynamics>[1]['metric'];
/** Subset of metrics that support per-bucket drill-down on the backend. */
const DRILLDOWN_METRICS: DynamicsMetric[] = [
  // 'plan-done-debt-vf' — drill modal disabled; navigate with Zoom + scrollbar instead.
  'bug-flow',
  'bug-arrival',
  'mttr',
  'escaped',
  'reopens',
  'predictability',
  'bug-origin',
];

interface Props {
  id: string;
  title: string;
  hint: string;
  /**
   * Dense setup hint (board, sync, grouping). Prefer short text; rendered in a compact disclosure, not as a banner.
   */
  needs?: string;
  metric: DynamicsMetric;
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  /**
   * Render the chart body given the `points`, optional `onPointClick`, and optional
   * server `diagnostics` (e.g. QA throughput definition edges).
   */
  children: (
    points: Array<Record<string, number | string | null>>,
    onPointClick?: (bucketLabel: string) => void,
    diagnostics?: DynamicsDiagnostics | null
  ) => React.ReactNode;
  /**
   * Called once with `{ hasData }` every time this card finishes loading so
   * the parent (e.g. DynamicsSection's "Show only cards with data" toggle)
   * can tally how many cards are actually rendering numbers.
   */
  onDataStatus?: (status: { metric: DynamicsMetric; hasData: boolean; loading: boolean }) => void;
  /**
   * When true (default for all cards except A), chart X-axis uses zoom + horizontal scrollbar
   * like Dynamics · Plan vs Done.
   */
  chartZoom?: boolean;
  /**
   * Pixel height passed to Recharts `ResponsiveContainer` in the child chart — must match for Y zoom.
   * @default 320
   */
  chartHeight?: number;
  /**
   * When set (e.g. MTTR dynamics), shows an insight bulb beside fullscreen — opens a compact modal with bullets.
   */
  insights?: string[];
  insightsTitle?: string;
  /**
   * Extra control(s) rendered in the card header's right-hand action group,
   * before the insight bulb and the expand button. Used by hour-displaying
   * cards (MTTR, etc.) to hang the shared Hours/Days toggle off the frame
   * without each card recreating its own header.
   */
  headerExtra?: React.ReactNode;
}

export const DynamicsChartFrame: React.FC<Props> = ({
  id,
  title,
  hint,
  needs,
  metric,
  groupBy,
  lastN,
  resolvedPeriod,
  children,
  onDataStatus,
  chartZoom: chartZoomProp,
  chartHeight = 320,
  insights,
  insightsTitle,
  headerExtra,
}) => {
  const chartZoom = chartZoomProp ?? id !== 'A';
  const boardId = useDefaultBoardId();
  // Subscribe to the shared batch first — if the current page already fetched
  // this metric in one shot, skip the per-card request entirely.
  const batch = useDynamicsBatchEntry(metric);
  const [points, setPoints] = useState<Array<Record<string, number | string | null>>>([]);
  const [diagnostics, setDiagnostics] = useState<DynamicsDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [drilldownLabel, setDrilldownLabel] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [insightModalOpen, setInsightModalOpen] = useState(false);

  const canExpand = !loading && !error && points.length > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (insights && insights.length && insightModalOpen) {
        setInsightModalOpen(false);
        return;
      }
      if (expanded) setExpanded(false);
    };
    if (!(expanded || insightModalOpen)) return;
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, insightModalOpen, insights]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);

  const closeExpanded = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    // Batch path: values are served by the parent provider.
    if (batch.served) {
      setPoints(batch.points ?? []);
      setDiagnostics(batch.diagnostics ?? null);
      setError(batch.error?.message ?? null);
      setErrorCode(batch.error?.code ?? null);
      setLoading(false);
      return;
    }
    // Fallback: individual fetch (still used by tests or when a card is
    // mounted outside of `DynamicsBatchProvider`).
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      try {
        const res = await QualityApi.getDynamics(
          { period: null, resolvedPeriod },
          { metric, groupBy, lastN, boardId }
        );
        if (!cancelled) {
          setPoints(res.points ?? []);
          setDiagnostics(res.diagnostics ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setPoints([]);
          setDiagnostics(null);
          const asAny = err as { code?: string; message?: string } | undefined;
          setError(asAny?.message || 'Failed to load');
          setErrorCode(asAny?.code || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metric,
    groupBy,
    lastN,
    resolvedPeriod?.startDate,
    resolvedPeriod?.endDate,
    boardId,
    batch.served,
    batch.loading,
    batch.points,
    batch.diagnostics,
    batch.error,
  ]);

  useEffect(() => {
    if (onDataStatus) {
      onDataStatus({
        metric,
        hasData: !loading && !error && (points?.length ?? 0) > 0,
        loading,
      });
    }
  }, [metric, loading, error, points, onDataStatus]);

  const footerMeta = useMemo(() => {
    if (!diagnostics || diagnostics.bucketsCount === 0) return null;
    const buckets = diagnostics.bucketsCount;
    const periodWord =
      groupBy === 'sprint'
        ? buckets === 1
          ? 'sprint column'
          : 'sprint columns'
        : groupBy === 'release'
          ? buckets === 1
            ? 'release column'
            : 'release columns'
          : groupBy === 'week'
            ? buckets === 1
              ? 'week column'
              : 'week columns'
            : buckets === 1
              ? 'month column'
              : 'month columns';
    return `Axis: ${buckets} ${periodWord}`;
  }, [diagnostics, groupBy]);

  const pointClickSupported = DRILLDOWN_METRICS.includes(metric);
  const handlePointClick = pointClickSupported
    ? (bucketLabel: string) => setDrilldownLabel(bucketLabel)
    : undefined;

  const chartBlock = (pts: Array<Record<string, number | string | null>>) =>
    chartZoom && pts.length > 1 ? (
      <DynamicsChartZoomShell data={pts} chartHeight={chartHeight}>
        {(visible) => children(visible, handlePointClick, diagnostics)}
      </DynamicsChartZoomShell>
    ) : (
      children(pts, handlePointClick, diagnostics)
    );

  const insightBullets =
    insights && insights.length ? insights.filter((s) => s && s.trim()) : undefined;

  const insightButton = insightBullets?.length ? (
    <button
      type="button"
      className={[
        'dynamics-card__insight-btn',
        insightModalOpen ? 'dynamics-card__insight-btn--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => setInsightModalOpen(true)}
      title="Insights you can take from this chart"
      aria-label="Insights from this chart"
      aria-expanded={insightModalOpen}
      aria-haspopup="dialog"
    >
      <Lightbulb size={16} aria-hidden strokeWidth={2} />
    </button>
  ) : null;

  const insightModal =
    insightBullets?.length && insightModalOpen
      ? createPortal(
          <div
            className="dynamics-card__insight-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`dynamics-${id}-insights-heading`}
            onClick={(e) => {
              if (e.target === e.currentTarget) setInsightModalOpen(false);
            }}
          >
            <div className="dynamics-card__insight-panel" onClick={(e) => e.stopPropagation()}>
              <h4 id={`dynamics-${id}-insights-heading`}>
                {insightsTitle ?? 'What this chart helps you spot'}
              </h4>
              <ul>
                {insightBullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
              <button
                type="button"
                className="dynamics-card__insight-panel-close"
                onClick={() => setInsightModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      id={`dynamics-${id}`}
      className={['chart-card', 'dynamics-card', chartZoom ? 'dynamics-card--chart-zoom' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="chart-header dynamics-card__header-with-actions">
        <div className="dynamics-card__header-main">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {title}
            <span className="stat-hint-icon" title={hint} aria-label={`About ${title}`}>
              <Info size={14} />
            </span>
          </h3>
          {needs && (
            <details className="dynamics-card__needs-details">
              <summary className="dynamics-card__needs-summary">Data setup</summary>
              <p className="dynamics-card__needs-body">{needs}</p>
              <p className="dynamics-card__needs-more">
                <Link
                  to="/settings?tab=project&section=quality-settings"
                  className="dynamics-empty-diagnostic__link"
                >
                  Quality settings
                </Link>
                <span aria-hidden> — </span>
                map Jira statuses to these lines.
              </p>
            </details>
          )}
        </div>
        <div className="dynamics-card__header-actions">
          {headerExtra}
          {insightButton}
          <button
            type="button"
            className="dynamics-card__expand-btn"
            disabled={!canExpand}
            title={canExpand ? 'Expand chart' : 'Load data to expand'}
            aria-label={canExpand ? 'Expand chart full screen' : 'Expand unavailable'}
            onClick={() => canExpand && setExpanded(true)}
          >
            <Maximize2 size={16} aria-hidden />
          </button>
        </div>
      </div>
      <div className="dynamics-card__body">
        {loading ? (
          <p className="quality-breakdown__empty">Loading…</p>
        ) : error ? (
          <ErrorState message={error} code={errorCode} />
        ) : points.length === 0 ? (
          <DiagnosticEmptyState diagnostics={diagnostics} groupBy={groupBy} />
        ) : (
          <>
            <DynamicsChartLayoutProvider value={{ variant: 'inline' }}>
              {chartBlock(points)}
            </DynamicsChartLayoutProvider>
            {footerMeta && (
              <p className="dynamics-card__footer-meta" title="How many buckets are plotted on X">
                {footerMeta}
              </p>
            )}
          </>
        )}
      </div>
      {expanded &&
        canExpand &&
        createPortal(
          <div
            className="dynamics-expand-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeExpanded();
            }}
          >
            <div
              className="dynamics-expand-panel chart-card dynamics-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="dynamics-expand-panel__head">
                <h3 className="dynamics-expand-panel__title">{title}</h3>
                <div className="dynamics-card__header-actions">
                  {headerExtra}
                  {insightButton}
                  <button
                    type="button"
                    className="dynamics-card__expand-btn dynamics-expand-panel__close"
                    onClick={closeExpanded}
                    title="Close"
                    aria-label="Close expanded chart"
                  >
                    <Minimize2 size={16} aria-hidden />
                  </button>
                </div>
              </div>
              <div className="dynamics-expand-panel__body dynamics-card__body">
                <DynamicsChartLayoutProvider value={{ variant: 'expanded' }}>
                  {chartBlock(points)}
                  {footerMeta && (
                    <p
                      className="dynamics-card__footer-meta"
                      title="How many buckets are plotted on X"
                    >
                      {footerMeta}
                    </p>
                  )}
                </DynamicsChartLayoutProvider>
              </div>
            </div>
          </div>,
          document.body
        )}
      {insightModal}
      {drilldownLabel && pointClickSupported && (
        <BucketDrilldownModal
          metric={metric as Parameters<typeof QualityApi.getBucketDrillDown>[1]['metric']}
          groupBy={groupBy}
          bucketLabel={drilldownLabel}
          chartTitle={title}
          points={points}
          dynamicsDiagnostics={diagnostics}
          resolvedPeriod={resolvedPeriod}
          boardId={boardId}
          onClose={() => setDrilldownLabel(null)}
        />
      )}
    </div>
  );
};

const DiagnosticEmptyState: React.FC<{
  diagnostics: DynamicsDiagnostics | null;
  groupBy: 'sprint' | 'release' | 'week' | 'month';
}> = ({ diagnostics, groupBy }) => {
  const reason = diagnostics?.reason ?? null;
  if (reason === 'no-board' || reason === 'no-board-fallback') {
    return (
      <div className="quality-breakdown__empty dynamics-empty-diagnostic">
        <p>
          <strong>No default board selected.</strong> Dynamics needs a Jira board to scope sprints.
        </p>
        <p>
          Open{' '}
          <Link to="/releases" className="dynamics-empty-diagnostic__link">
            Releases → Sprint Plan
          </Link>
          , choose a board and click <em>Save as default board</em>. Then return here.
        </p>
      </div>
    );
  }
  if (reason === 'no-sprints-from-board') {
    return (
      <p className="quality-breakdown__empty">
        The selected board returned no sprints. Check that the board is a Scrum board with at least
        one closed/active sprint.
      </p>
    );
  }
  if (reason === 'no-sprints-with-dates') {
    return (
      <p className="quality-breakdown__empty">
        Sprints on this board have no start/end dates — Dynamics needs dated sprints to build the
        time axis.
      </p>
    );
  }
  if (reason === 'no-sprints-in-period') {
    return (
      <p className="quality-breakdown__empty">
        No sprints overlap the current Period. Widen the Period or switch Group by to{' '}
        <strong>{groupBy === 'sprint' ? 'Week or Month' : 'Week'}</strong>.
      </p>
    );
  }
  if (reason === 'no-releases-in-period') {
    return (
      <p className="quality-breakdown__empty">
        No releases (fix versions) overlap the current Period. Try widening the Period or switching
        Group by.
      </p>
    );
  }
  if (reason === 'no-buckets-in-period') {
    return (
      <p className="quality-breakdown__empty">
        Period is narrower than the bucket size. Widen the Period or switch to a smaller{' '}
        <em>Group by</em>.
      </p>
    );
  }
  return (
    <p className="quality-breakdown__empty">
      Not enough data. Try increasing &quot;Last N&quot; or choose a broader Group by.
    </p>
  );
};

const ErrorState: React.FC<{ message: string; code: string | null }> = ({ message, code }) => {
  if (code === 'quality.escape_cause_field_not_configured' || /escape/i.test(message)) {
    return (
      <div className="quality-breakdown__empty dynamics-empty-diagnostic">
        <p>
          <strong>Escape root-cause field is not configured.</strong>
        </p>
        <p>
          Open <Link to="/settings?tab=project&section=quality-settings">Settings → Quality</Link>{' '}
          and pick the Jira custom field that classifies escape root cause.
        </p>
      </div>
    );
  }
  return <p className="quality-breakdown__empty">Failed to load: {message}</p>;
};
